import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Globe2, ShieldCheck, ArrowLeftRight, Stamp } from "lucide-react";
import SectionHeader from "./SectionHeader";
import Crest from "./Crest";

const PRIVILEGES = [
  { icon: Globe2, title: "Visa-free movement", copy: "One document opens 46 visa-free states, led by the Kingdom of Kush, across all five regions of the continent." },
  { icon: ShieldCheck, title: "Biometric & secured", copy: "A sovereign identity seal, verifiable at every border post of the Union." },
  { icon: ArrowLeftRight, title: "Free trade rights", copy: "Youth and business people of every industry trade freely among ourselves." },
  { icon: Stamp, title: "Already in motion", copy: "The passport has been issued and is traveling across the continent at this moment." },
];

const REGIONS_STRIP = ["WEST", "EAST", "SOUTHERN", "NORTH", "CENTRAL"];

export default function Passport() {
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 120, damping: 16 });
  const sry = useSpring(ry, { stiffness: 120, damping: 16 });
  const sheenX = useTransform(sry, [-12, 12], ["-40%", "140%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 16);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 12);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <section id="passport" className="relative py-20 lg:py-32 noise-overlay" data-testid="passport-section">
      <div className="absolute inset-0 gold-glow opacity-60" />
      <div className="relative px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <SectionHeader
            kicker="The Unifier Document"
            title="One Passport for One Continent"
            copy="Issued under the seal of the Kingdom of Kush, the United States of Africa passport is the document that binds the Union — carried by youth, traders, and the diaspora alike."
          />
          <div className="mt-10 space-y-6">
            {PRIVILEGES.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="flex gap-5 items-start"
                data-testid={`passport-privilege-${i + 1}`}
              >
                <div className="shrink-0 w-11 h-11 rounded-full border border-[#00A651]/30 flex items-center justify-center text-[#00A651]">
                  <p.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-base text-[#F7F5F0]">{p.title}</h3>
                  <p className="text-sm text-[#8A99AD] mt-1 leading-relaxed">{p.copy}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="relative" style={{ perspective: 1200 }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              onMouseMove={onMove}
              onMouseLeave={onLeave}
              style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
              className="relative aspect-[1.45/1] w-full max-w-lg mx-auto rounded-2xl border border-[#00A651]/40 bg-gradient-to-br from-[#1A1F26] via-[#121519] to-[#0A0C0E] shadow-[0_40px_80px_rgba(0,0,0,0.6)] overflow-hidden"
              data-testid="passport-card"
            >
              <div className="absolute inset-0 gold-glow" />
              <motion.div
                style={{ left: sheenX }}
                className="absolute top-0 bottom-0 w-1/3 bg-gradient-to-r from-transparent via-[#F7F5F0]/10 to-transparent -skew-x-12 pointer-events-none"
              />
              <div className="relative h-full flex flex-col justify-between p-6 sm:p-8" style={{ transform: "translateZ(40px)" }}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-display font-extrabold tracking-tight text-[#F7F5F0] text-sm sm:text-base leading-tight">
                      UNITED STATES OF AFRICA
                    </p>
                    <p className="font-official text-[9px] sm:text-[10px] tracking-[0.35em] text-[#00A651] mt-1.5 uppercase">
                      Passport — Africa Without Border
                    </p>
                  </div>
                  <Crest className="w-12 h-12 sm:w-14 sm:h-14 shrink-0" />
                </div>
                <div className="flex items-end justify-between gap-4">
                  <div className="space-y-2.5">
                    <div className="w-10 h-7 rounded border border-[#00A651]/50 bg-[#00A651]/10 grid grid-cols-3 grid-rows-2 gap-px p-px">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <span key={i} className="bg-[#00A651]/30" />
                      ))}
                    </div>
                    <div className="font-official text-[9px] sm:text-[10px] tracking-[0.18em] text-[#8A99AD] space-y-1 uppercase">
                      <p>Passport No <span className="text-[#00A651]">USA-KK-000001</span></p>
                      <p>Holder <span className="text-[#F7F5F0]">Citizen of Africa</span></p>
                      <p>Authority <span className="text-[#F7F5F0]">Kingdom of Kush</span></p>
                    </div>
                  </div>
                  <p className="font-serif-display italic text-[#00A651]/70 text-lg sm:text-xl">Kush</p>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden flex">
                  <span className="flex-1 bg-[#009B4D]" />
                  <span className="flex-1 bg-[#FFD700]" />
                  <span className="flex-1 bg-[#CE1126]" />
                </div>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-5 -right-2 sm:right-4 rounded-full border border-[#00A651]/30 bg-[#121519]/90 backdrop-blur px-4 py-2 font-official text-[10px] tracking-[0.2em] text-[#00A651] uppercase"
            data-testid="passport-chip-visafree"
          >
            Visa-Free • 46 States
          </motion.div>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute -bottom-5 -left-2 sm:left-4 rounded-full border border-[#009B4D]/50 bg-[#121519]/90 backdrop-blur px-4 py-2 font-official text-[10px] tracking-[0.2em] text-emerald-300/80 uppercase"
            data-testid="passport-chip-biometric"
          >
            Biometric Secured
          </motion.div>

          <div className="mt-14 flex flex-wrap justify-center gap-3" data-testid="passport-regions-strip">
            {REGIONS_STRIP.map((r) => (
              <span key={r} className="font-official text-[10px] tracking-[0.25em] text-[#8A99AD] border border-[#00A651]/15 rounded-full px-4 py-1.5">
                {r} — VISA-FREE
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
